import React from 'react'

import { Breakpoint, pluralize, useModalContext } from '@sketch/components'

import {
  GetStorageItemsDocument,
  GetStorageItemsQuery,
  ProjectDetailsFragment,
  ShareListItemFragment,
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import {
  BaseStorageItemsLayoutProps,
  StorageItemsLayout,
} from '../../../storageItems/components'
import { useSortingSettings } from '../../../shares/hooks/useSortSettings'
import { useSearch } from '@sketch/utils'
import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
} from '../../../shares/components/DocumentItem'
import {
  ActionTableCell,
  FocusLink,
  Footer,
  HeaderTableCell,
} from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'
import { ResponsiveValues } from '@sketch/global-styles'
import {
  isDraftsRoute,
  isProjectRoute,
  routes,
  useFlag,
  useFolderTerm,
  useUserProfile,
} from '@sketch/modules-common'
import { useHistory, useRouteMatch } from 'react-router'
import EmptyState from '../../../shares/components/EmptyStateOld'
import { ErrorHandler } from '@sketch/tracing'
import {
  DocumentItemDropdown,
  DocumentItemDropdownProps,
} from '../../../shares/components/DocumentItemDropdown'
import { updatePinnedShares } from '../../../shares/components/ShareActionsDropdown/pinShare.cache'
import { ShareTrashedModal } from '../../../shares/components/ShareTrashedModal'
import {
  DraggableShareItem,
  DraggableShareItemProps,
} from '../../../shares/components/DraggableShareItem'
import { useDropProjectToShare } from '../../../shares/hooks/useDropProjectToShare'
import { useDropShareToNestedProject } from '../../../shares/hooks/useDropShareToNestedProject'
import { useSelectionContext } from '../../../storageItems/components/SelectionContext'
import { AnimatedSelectionBottomSink } from '../../../storageItems/components/SelectionBottomSink'

import {
  GetStorageItemsProps,
  normalizePropsToVariables,
} from '../../hooks/useProject'

interface ProjectDocumentListProps
  extends BaseStorageItemsLayoutProps<ShareListItemFragment> {
  workspace: WorkspaceMinimalFragment
  project: ProjectDetailsFragment
  action?: (item: StorageItemFragment) => (() => void) | 'link'
  queryVariables: GetStorageItemsProps
}

type DocumentItemDropdownShare = DocumentItemDropdownProps['share']

export const ProjectDocumentList = (props: ProjectDocumentListProps) => {
  const {
    workspace,
    project,
    totalCount = 0,
    onLoadMore,
    items,
    customColumns,
    queryVariables,
  } = props

  const history = useHistory()

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  // TODO: Remove `showProjectName` prop when `nested-projects` is 100% enabled
  // https://linear.app/sketch/issue/SWEB-768/cleanup-after-nested-projects-ff-is-100percent-enabled
  const showProjectName = true

  const isDesignSystemsOn = useFlag('design-systems')

  const { showModal } = useModalContext()
  const { sort } = useSortingSettings()
  const { searchDebounced: search } = useSearch()

  // Route match
  const { path } = useRouteMatch()

  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)
  const isValidViewToCreateProject = isProjectView || isDraftView

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })

  const folderTerm = useFolderTerm()

  const dropShareToNestedProject = useDropShareToNestedProject({
    projectIdentifier: project.identifier,
    storageItemsQueryVariables: normalizePropsToVariables(queryVariables, sort),
  })
  const dropProjectToShare = useDropProjectToShare({
    projectIdentifier: project.identifier,
    storageItemsQueryVariables: normalizePropsToVariables(queryVariables, sort),
  })

  // Selection
  const { toggleItemSelection, showSelectionCheckbox, isSelected } =
    useSelectionContext()

  /**
   * This action is triggered when the user clicks the DocumentItem
   */
  const actionCreator = (item: StorageItemFragment) => {
    const isShare = item.__typename === 'Share'
    if (isShare && item.deletedAt) {
      return () => showModal(ShareTrashedModal, { share: item })
    }

    return 'link'
  }

  const renderDropdown = (item: StorageItemFragment) => {
    // Rendering a Share Item
    const share = items.find(
      share => share.identifier === item.identifier
    ) as DocumentItemDropdownShare

    if (!share) {
      ErrorHandler.shouldNeverHappen(
        'share should always be present rendering the ProjectDocumentDropdown'
      )
      return null
    }

    return (
      // TODO: Refactor DocumentItemDropdown, make it part of StorageItemsLayout.
      // https://linear.app/sketch/issue/SWEB-1362
      <DocumentItemDropdown
        share={share}
        workspaceIdentifier={workspace.identifier}
        workspace={workspace}
        pinDocumentConfig={{
          shareIdentifier: share.identifier,
          pinned: !!share?.pinnedByCurrentUserAt,
          update(cache, { data }) {
            if (!data) return

            updatePinnedShares<GetStorageItemsQuery>({
              shareId: share.identifier,
              pickPagination: data => data?.project?.storageItems,
              readData: () => {
                const result = cache.readQuery<GetStorageItemsQuery>({
                  query: GetStorageItemsDocument,
                  variables: queryVariables,
                })
                return result ?? undefined
              },
              writeData: newData => {
                cache.writeQuery({
                  query: GetStorageItemsDocument,
                  variables: queryVariables,
                  data: newData,
                })
              },
              sort,
            })
          },
        }}
      />
    )
  }

  const renderGridItem = (item: ShareListItemFragment, isMobile: boolean) => {
    const action = actionCreator(item)
    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    if (!item) return null

    const commonDraggableShareItemProps: DraggableShareItemProps<ShareListItemFragment> =
      {
        presentation: 'grid',
        workspaceIdentifier: workspace.identifier,
        userIdentifier: userIdentifier,
        item: item,
        renderDropdown,
        showProjectName: showProjectName,
        draggable:
          !isMobile && userCanUpdateSettings && isValidViewToCreateProject,
        onShareDropped: dropShareToNestedProject,
        onProjectDropped: dropProjectToShare,
        selected: isSelected(item),
        onSelectChange() {
          toggleItemSelection(item)
        },
        showSelectionCheckbox,
      }

    if (action !== 'link') {
      return (
        <DraggableShareItem
          {...commonDraggableShareItemProps}
          onClick={action}
        />
      )
    }

    return (
      <FocusLink
        draggable={false}
        to={{
          pathname: routes.SHARE_VIEW.create({ shareID: item.identifier }),
          state: { componentsView: true },
        }}
      >
        <DraggableShareItem {...commonDraggableShareItemProps} />
      </FocusLink>
    )
  }

  const handleListItemClick = (
    event: React.MouseEvent<HTMLElement>,
    item: StorageItemFragment
  ) => {
    const link = routes.SHARE_VIEW.create({
      shareID: item.identifier,
    })

    if (event.metaKey) {
      /* Simulate the link opening with meta key */
      window.open(link, '_blank')
    } else {
      history.push(link)
    }
  }

  const renderListItem = (item: ShareListItemFragment, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    return (
      <DraggableShareItem
        key={item.identifier}
        presentation="list"
        workspaceIdentifier={workspace.identifier}
        userIdentifier={userIdentifier}
        item={item}
        renderDropdown={renderDropdown}
        showProjectName={showProjectName}
        draggable={
          !isMobile && userCanUpdateSettings && isValidViewToCreateProject
        }
        onShareDropped={dropShareToNestedProject}
        onProjectDropped={dropProjectToShare}
        onClick={event =>
          action === 'link' ? handleListItemClick(event, item) : action()
        }
        selected={isSelected(item)}
        onSelectChange={() => {
          toggleItemSelection(item)
        }}
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    // for design systems search: check if we're on the library route and if empty count for a special message
    if (isWorkspaceLibrariesRoute && totalCount === 0 && isDesignSystemsOn) {
      return (
        <EmptyState
          search={search}
          title="No Libraries"
          description="No Libraries added yet."
          icon="link"
        />
      )
    }

    const commonStorageItemsLayoutProps = {
      items: items,
      onLoadMore: onLoadMore,
      customColumns: customColumns,
      totalCount: totalCount,
    }

    if (documentsLayout === 'LIST') {
      const headerLabel = pluralize('Document', 'Documents', totalCount)

      return (
        <StorageItemsLayout
          header={[
            {
              label: `${totalCount} ${headerLabel}`,
              customCell: HeaderTableCell,
            },
            { label: folderTerm.upper, customCell: HeaderTableCell },
            { label: 'Last Updated', customCell: HeaderTableCell },
            { label: 'Updated By', customCell: HeaderTableCell },
            { label: '', customCell: ActionTableCell },
          ]}
          Skeleton={DocumentTableItemPlaceholder}
          renderItem={item => renderListItem(item, isMobile)}
          {...commonStorageItemsLayoutProps}
        />
      )
    }

    return (
      <StorageItemsLayout
        Skeleton={DocumentGridItemPlaceholder}
        renderItem={item => renderGridItem(item, isMobile)}
        {...commonStorageItemsLayoutProps}
      />
    )
  }

  return (
    <>
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>

      <AnimatedSelectionBottomSink workspaceIdentifier={workspace.identifier} />
      <Footer />
    </>
  )
}
